import { useState, useEffect } from "react";
import {
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaClipboardList,
} from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdCheckCircle, MdError } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../../components/context/apiContext";

const FloatingInput = ({
  label,
  type = "text",
  value,
  onChange,
  error,
  success,
  prefix,
  icon: Icon,
}) => {
  const [focused, setFocused] = useState(false);
  const hasValue = value && value.length > 0;
  const isFloating = focused || hasValue;

  return (
    <div className="relative">
      <div
        className="flex items-center w-full rounded-xl border-2 transition-all duration-300 overflow-hidden"
        style={{
          borderColor: error
            ? "#ef4444"
            : success
              ? "#10b981"
              : focused
                ? "#00ADED"
                : "#e2e8f0",
          background: focused ? "#fff" : "#f8fafc",
          boxShadow: focused ? "0 0 0 4px rgba(0,173,237,0.10)" : "none",
        }}
      >
        {/* Leading icon */}
        {Icon && (
          <div className="pl-4 shrink-0">
            <Icon
              size={15}
              style={{
                color: focused
                  ? "#00ADED"
                  : error
                    ? "#ef4444"
                    : success
                      ? "#10b981"
                      : "#cbd5e1",
              }}
              className="transition-colors duration-200"
            />
          </div>
        )}

        {/* Phone prefix */}
        {prefix && (
          <div className="flex items-center gap-1.5 pl-3 pr-3 py-4 border-r border-slate-200 shrink-0">
            <span className="text-base leading-none">🇮🇳</span>
            <span className="text-sm font-semibold text-slate-600">+91</span>
          </div>
        )}

        <div className="relative flex-1">
          <label
            className="absolute transition-all duration-200 pointer-events-none font-medium z-10"
            style={{
              left: "16px",
              top: isFloating ? "7px" : "50%",
              transform:
                isFloating && type !== "textarea"
                  ? "translateY(0)"
                  : isFloating
                    ? "translateY(0)"
                    : "translateY(-50%)",
              fontSize: isFloating ? "9px" : "13px",
              color: error
                ? "#ef4444"
                : success
                  ? "#10b981"
                  : focused
                    ? "#00ADED"
                    : "#94a3b8",
              letterSpacing: isFloating ? "0.07em" : "0",
              textTransform: isFloating ? "uppercase" : "none",
            }}
          >
            {label}
          </label>
          {type === "textarea" ? (
            <textarea
              value={value}
              onChange={onChange}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              rows={4}
              className="w-full bg-transparent outline-none resize-none px-4 pt-6 pb-3 text-slate-800 text-sm no-border-textarea"
            />
          ) : (
            <input
              type={type}
              value={value}
              onChange={onChange}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              className="w-full bg-transparent outline-none px-4 pt-5 pb-2 text-slate-800 text-sm h-14"
            />
          )}
        </div>

        {/* Status icon */}
        <div className="pr-4 shrink-0 flex items-center">
          {error && <MdError size={18} color="#ef4444" />}
          {!error && success && <MdCheckCircle size={18} color="#10b981" />}
        </div>
      </div>

      {error && (
        <p className="text-red-500 text-xs mt-1.5 pl-1 flex items-center gap-1">
          <MdError size={12} /> {error}
        </p>
      )}
    </div>
  );
};

export default function AppointmentForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    description: "",
  });
  const { doctorList, setSelectedDoctor, setSelectedSpecialty } = useApi();
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { doctorName } = location.state || {};

  useEffect(() => {
    if (doctorList?.length === 0) {
      setSelectedDoctor("");
      setSelectedSpecialty("");
    }
  }, [doctorList]);

  const validate = (field, value) => {
    const newErrors = { ...errors };
    const newSuccess = { ...success };
    if (field === "name") {
      if (!value.trim()) newErrors.name = "Full name is required";
      else if (value.trim().length < 3)
        newErrors.name = "Name must be at least 3 characters";
      else {
        delete newErrors.name;
        newSuccess.name = true;
      }
    }
    if (field === "email") {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value) newErrors.email = "Email address is required";
      else if (!re.test(value)) newErrors.email = "Enter a valid email address";
      else {
        delete newErrors.email;
        newSuccess.email = true;
      }
    }
    if (field === "phone") {
      const digits = value.replace(/\D/g, "");
      if (!digits) newErrors.phone = "Phone number is required";
      else if (digits.length !== 10)
        newErrors.phone = "Enter a valid 10-digit mobile number";
      else {
        delete newErrors.phone;
        newSuccess.phone = true;
      }
    }
    if (field === "description") {
      if (!value.trim())
        newErrors.description = "Please describe your appointment needs";
      else {
        delete newErrors.description;
        newSuccess.description = true;
      }
    }
    setErrors(newErrors);
    setSuccess(newSuccess);
  };

  const handleChange = (field) => (e) => {
    let val = e.target.value;
    if (field === "phone") val = val.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, [field]: val }));
    if (errors[field] || success[field]) validate(field, val);
  };

  const handleSubmit = async () => {
    // Build all errors in one pass
    const newErrors = {};
    const newSuccess = {};

    if (!form.name.trim()) newErrors.name = "Full name is required";
    else if (form.name.trim().length < 3)
      newErrors.name = "Name must be at least 3 characters";
    else newSuccess.name = true;

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email) newErrors.email = "Email address is required";
    else if (!emailRe.test(form.email))
      newErrors.email = "Enter a valid email address";
    else newSuccess.email = true;

    const digits = form.phone.replace(/\D/g, "");
    if (!digits) newErrors.phone = "Phone number is required";
    else if (digits.length !== 10)
      newErrors.phone = "Enter a valid 10-digit mobile number";
    else newSuccess.phone = true;

    if (!form.description.trim())
      newErrors.description = "Please describe your appointment needs";
    else newSuccess.description = true;

    setErrors(newErrors);
    setSuccess(newSuccess);

    if (Object.keys(newErrors).length > 0) return;

    // POST API call
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name.trim());
      formData.append("email", form.email);
      formData.append("docname", doctorName);
      formData.append("phone", `91${form.phone.replace(/\D/g, "")}`);
      formData.append("query", form.description.trim());
      const response = await fetch(
        "https://asianheartinstitute.org/sms/mailing.php",
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) throw new Error("Submission failed");

      // setSubmitted(true);

      navigate("/appointment-success");
    } catch (error) {
      setErrors({ api: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: "linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%)",
        }}
      >
        <div
          className="bg-white rounded-3xl p-10 max-w-md w-full text-center"
          style={{
            boxShadow:
              "0 24px 64px rgba(0,173,237,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: "linear-gradient(135deg, #00ADED, #006fa3)" }}
          >
            <MdCheckCircle size={40} color="white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            Appointment Booked Successfully!
          </h2>
          <p className="text-slate-500 text-sm mb-6 leading-relaxed">
            Your appointment has been booked successfully.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setForm({ name: "", email: "", phone: "", description: "" });
              setErrors({});
              setSuccess({});
            }}
            className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg"
            style={{ background: "linear-gradient(135deg, #00ADED, #006fa3)" }}
          >
            Book Another Appointment
          </button>
        </div>
      </div>
    );
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; font-family: system-ui, -apple-system, sans-serif; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        @keyframes pulse-dot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.85); } }
        .card { animation: fadeUp 0.5s ease both; }
        .submit-btn { background: linear-gradient(135deg, #00ADED 0%, #006fa3 100%); transition: all 0.3s ease; }
        .submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(0,173,237,0.38); }
        .submit-btn:active:not(:disabled) { transform: translateY(0); }
        .submit-btn:disabled { opacity: 0.85; cursor: not-allowed; }
        .accent-bar { background: linear-gradient(90deg, #00ADED, #38bdf8, #00ADED); background-size: 200% 100%; animation: shimmer 2.5s linear infinite; }
        .pulse-dot { animation: pulse-dot 2s ease infinite; }
        textarea, input { font-family: system-ui, -apple-system, sans-serif;
        .no-border-textarea { border: none !important; outline: none !important; } }
      `}</style>

      <div
        className="min-h-screen flex items-center justify-center p-4 py-10"
        style={{
          background:
            "linear-gradient(135deg, #f0f9ff 0%, #dbeafe 50%, #f0f9ff 100%)",
        }}
      >
        {/* Decorative blobs */}
        <div
          className="fixed top-0 left-0 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(0,173,237,0.08) 0%, transparent 70%)",
            transform: "translate(-40%, -40%)",
          }}
        />
        <div
          className="fixed bottom-0 right-0 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(0,173,237,0.08) 0%, transparent 70%)",
            transform: "translate(40%, 40%)",
          }}
        />

        <div className="card w-full max-w-lg">
          <button
            onClick={handleGoBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              background: "white",
              border: "none",
              borderRadius: "10px",
              padding: "8px 14px 8px 10px",
              marginBottom: "16px",
              cursor: "pointer",
              color: "#00ADED",
              fontSize: "13.5px",
              fontWeight: "600",
              fontFamily: "inherit",
              boxShadow:
                "0 2px 8px rgba(0,173,237,0.10), 0 1px 3px rgba(0,0,0,0.05)",
              transition: "all 0.18s ease",
              letterSpacing: "0.01em",
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
            Go Back
          </button>
          <div
            className="bg-white rounded-3xl overflow-hidden"
            style={{
              boxShadow:
                "0 24px 64px rgba(0,173,237,0.12), 0 4px 16px rgba(0,0,0,0.06)",
            }}
          >
            {/* Animated top bar */}
            <div className="accent-bar h-1.5 w-full" />

            <div className="p-8 md:p-10">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-slate-800 mb-3 leading-tight">
                  Schedule Your{" "}
                  <span style={{ color: "#00ADED" }}>Appointment</span>
                </h1>
                <p className="text-slate-400 text-sm leading-relaxed max-w-sm mx-auto">
                  Fill in your details below and our team will confirm your
                  booking within 24 hours.
                </p>
              </div>

              {/* Fields */}
              <div className="flex flex-col gap-4">
                <FloatingInput
                  label="Full Name"
                  value={form.name}
                  onChange={handleChange("name")}
                  error={errors.name}
                  success={success.name}
                  icon={FaUser}
                />
                <FloatingInput
                  label="Email Address"
                  type="email"
                  value={form.email}
                  onChange={handleChange("email")}
                  error={errors.email}
                  success={success.email}
                  icon={FaEnvelope}
                />
                <FloatingInput
                  label="Mobile Number"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange("phone")}
                  error={errors.phone}
                  success={success.phone}
                  prefix="+91"
                  icon={FaPhone}
                />
                <FloatingInput
                  label="Describe your needs (symptoms, specialty, preferred time…)"
                  type="textarea"
                  value={form.description}
                  onChange={handleChange("description")}
                  error={errors.description}
                  success={success.description}
                  icon={FaClipboardList}
                />

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="submit-btn w-full py-4 rounded-xl text-white font-bold text-sm tracking-wide flex items-center justify-center gap-2 mt-2"
                >
                  {loading ? (
                    <>
                      <AiOutlineLoading3Quarters
                        size={18}
                        className="animate-spin"
                      />
                      Processing…
                    </>
                  ) : (
                    <>
                      <FaCalendarAlt size={14} />
                      Submit
                    </>
                  )}
                </button>

                {/* <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                  <FaLock size={10} color="#94a3b8" />
                  Your information is secure and never shared with third parties
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
